import {
  readWorkflowPublishedInspection,
  type WorkflowPublishedInspection,
  type WorkflowPublishedInspectionSource,
  type WorkflowSavedPresence,
} from './workflow-published-inspection';
import type {
  WorkflowPublishedWorkDetailGroup,
  WorkflowPublishedWorkDetailRow,
  WorkflowPublishedWorkFact,
  WorkflowPublishedWorkInspection,
  WorkflowPublishedWorkRow,
  WorkflowPublishedWorkSection,
} from './workflow-published-work-inspection';

export type WorkflowPublishedProgressSectionId = 'progress' | 'stepLabels';
export type WorkflowPublishedProgressFact = WorkflowPublishedWorkFact;
export type WorkflowPublishedProgressDetailRow = WorkflowPublishedWorkDetailRow;
export type WorkflowPublishedProgressDetailGroup =
  WorkflowPublishedWorkDetailGroup;
export type WorkflowPublishedProgressRow = WorkflowPublishedWorkRow;
export interface WorkflowPublishedProgressSection extends Omit<
  WorkflowPublishedWorkSection,
  'id'
> {
  id: WorkflowPublishedProgressSectionId;
}
export interface WorkflowPublishedProgressInspection {
  context: string;
  state: 'available' | 'unavailable';
  provenance: WorkflowPublishedInspection['provenance'];
  target: { nodeIndex: number; nodeId: string | null };
  node: WorkflowPublishedWorkInspection['node'];
  issues: readonly string[];
  sections: Readonly<
    Record<WorkflowPublishedProgressSectionId, WorkflowPublishedProgressSection>
  >;
}

type Saved = Record<string, unknown>;
type Field = readonly [string, string];
const record = (raw: unknown): raw is Saved =>
  !!raw && typeof raw === 'object' && !Array.isArray(raw);
const object = (raw: unknown): Saved => (record(raw) ? raw : {});
const text = (raw: unknown): raw is string =>
  typeof raw === 'string' && !!raw.trim();
const copy = (raw: unknown): unknown => structuredClone(raw);
const presence = (owner: Saved, key: string): WorkflowSavedPresence =>
  !Object.hasOwn(owner, key)
    ? 'absent'
    : owner[key] === null
      ? 'null'
      : 'value';
function display(raw: unknown): string {
  if (raw === undefined) return 'Not declared';
  if (raw === null) return 'Explicitly null';
  if (typeof raw === 'string') return raw === '' ? 'Empty saved text' : raw;
  return JSON.stringify(raw) ?? 'Unsupported saved value';
}
function facts(
  raw: unknown,
  fields: readonly Field[],
  prefix: string,
): WorkflowPublishedProgressFact[] {
  const value = object(raw);
  return fields.map(([key, label]) => ({
    id: `${prefix}:${key}`,
    label,
    presence: presence(value, key),
    value: display(value[key]),
    issues: [],
    raw: copy(value[key]),
  }));
}
function extraIssues(raw: unknown, fields: readonly Field[]): string[] {
  return Object.keys(object(raw)).some(
    (key) => !fields.some(([name]) => name === key),
  )
    ? [
        'Additional saved properties are retained without interpreting their meaning.',
      ]
    : [];
}
function duplicateIssues(
  raw: unknown,
  all: readonly unknown[],
  key: string,
  label: string,
): string[] {
  const id = object(raw)[key];
  if (!text(id))
    return [`The saved ${label} is missing or has an unsupported shape.`];
  return all.filter((item) => object(item)[key] === id).length === 1
    ? []
    : [
        `Duplicate saved ${label}: ${id}. Every indexed declaration is retained independently.`,
      ];
}
function detail(
  raw: unknown,
  index: number,
  prefix: string,
  label: string,
  fields: readonly Field[],
  issues: readonly string[] = [],
): WorkflowPublishedProgressDetailRow {
  return {
    id: `${prefix}-${index}`,
    index,
    label,
    fields: facts(raw, fields, `${prefix}-${index}`),
    issues: [
      ...(!record(raw)
        ? [
            'This saved entry has an unsupported shape. Its original value is retained.',
          ]
        : []),
      ...extraIssues(raw, fields),
      ...issues,
    ],
    raw: copy(raw),
  };
}
function group(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  project: (
    raw: unknown,
    index: number,
    all: readonly unknown[],
  ) => WorkflowPublishedProgressDetailRow,
): WorkflowPublishedProgressDetailGroup {
  const raw = owner[key],
    savedPresence = presence(owner, key);
  const rows = Array.isArray(raw)
    ? raw.map((item, index) => project(item, index, raw))
    : [];
  const issues =
    savedPresence === 'value' && !Array.isArray(raw)
      ? [
          'The saved collection has an unsupported shape. Its original value is retained.',
        ]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence: savedPresence,
    rows,
    issues,
    raw: copy(raw),
    state:
      savedPresence !== 'value'
        ? 'absent'
        : issues.length || rows.some((row) => row.issues.length)
          ? 'retained-unknown'
          : rows.length
            ? 'available'
            : 'empty',
  };
}
function objectGroup(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  fields: readonly Field[],
  issues: readonly string[] = [],
): WorkflowPublishedProgressDetailGroup {
  const raw = owner[key],
    savedPresence = presence(owner, key);
  const rows =
    savedPresence === 'value'
      ? [detail(raw, 0, `${prefix}:${key}`, label, fields, issues)]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence: savedPresence,
    rows,
    issues: [],
    raw: copy(raw),
    state:
      savedPresence !== 'value'
        ? 'absent'
        : rows.some((row) => row.issues.length)
          ? 'retained-unknown'
          : 'available',
  };
}

interface SavedTarget {
  raw: Saved;
  index: number;
}
function uniqueTarget(
  collection: unknown,
  key: string,
  value: unknown,
  ownerUnique: boolean,
): SavedTarget | null {
  if (!ownerUnique || !text(value) || !Array.isArray(collection)) return null;
  const matches = collection
    .map((raw, index) => ({ raw, index }))
    .filter((item) => object(item.raw)[key] === value);
  return matches.length === 1 && record(matches[0].raw)
    ? { raw: matches[0].raw, index: matches[0].index }
    : null;
}
function referenceFacts(
  prefix: string,
  label: string,
  target: SavedTarget | null,
  caption: unknown,
): WorkflowPublishedProgressFact[] {
  return target
    ? [
        {
          id: `${prefix}:targetIndex`,
          label: `Saved ${label} index`,
          presence: 'value',
          value: String(target.index),
          issues: [],
          raw: target.index,
        },
        {
          id: `${prefix}:target`,
          label: `Unique saved ${label}`,
          presence: 'value',
          value: display(caption),
          issues: [],
          raw: copy(target.raw),
        },
      ]
    : [];
}
const bypassFields: readonly Field[] = [
  ['conditionCode', 'Condition code'],
  ['conditionVersion', 'Condition version'],
  ['outcomeCode', 'Outcome code'],
  ['reason', 'Saved reason'],
  ['processDataKey', 'Process data key'],
];
function progressRow(
  raw: unknown,
  document: Saved,
  ownerUnique: boolean,
): WorkflowPublishedProgressRow {
  const value = object(raw),
    bypass = object(value['bypass']);
  const fields: readonly Field[] = [['startMode', 'Saved start mode']];
  const issues = [
    ...(!text(value['startMode']) ||
    !['assigned_open', 'manual'].includes(value['startMode'])
      ? [
          'The saved start mode is unsupported. No historical or current default is substituted.',
        ]
      : []),
  ];
  const bypassIssues =
    value['bypass'] != null &&
    (!record(value['bypass']) ||
      !text(bypass['conditionCode']) ||
      !Number.isSafeInteger(bypass['conditionVersion']) ||
      Number(bypass['conditionVersion']) < 1 ||
      !text(bypass['outcomeCode']) ||
      typeof bypass['reason'] !== 'string' ||
      (bypass['processDataKey'] != null &&
        typeof bypass['processDataKey'] !== 'string'))
      ? [
          'The saved bypass declaration has an unsupported shape. It is not evaluated or repaired.',
        ]
      : [];
  const bypassGroup = objectGroup(
    value,
    'bypass',
    'Saved bypass declaration',
    'progress',
    bypassFields,
    bypassIssues,
  );
  if (bypassGroup.rows.length && bypass['processDataKey'] != null) {
    const target = uniqueTarget(
      object(document['processData'])['fields'],
      'key',
      bypass['processDataKey'],
      ownerUnique,
    );
    const row = bypassGroup.rows[0];
    row.fields = [
      ...row.fields,
      ...referenceFacts(
        'progress:bypass:processData',
        'process data field',
        target,
        target?.raw['label'],
      ),
    ];
    if (!target)
      row.issues = [
        ...row.issues,
        'The exact saved process data key does not identify one field. No caption or value is substituted.',
      ];
    if (row.issues.length) bypassGroup.state = 'retained-unknown';
  }
  const base = detail(
    raw,
    0,
    'progress',
    'Saved step progress',
    [...fields, ['bypass', 'Bypass']],
    issues,
  );
  // The full bypass remains in its own disclosure and original raw, rather than an expanded inline JSON fact.
  base.fields = facts(raw, fields, 'progress-0');
  return {
    ...base,
    description:
      'Saved start and bypass configuration only. No current progress state, bypass result or engine route is calculated.',
    state:
      base.issues.length || bypassGroup.state === 'retained-unknown'
        ? 'retained-unknown'
        : 'available',
    groups: [bypassGroup],
  };
}

const statusFields: readonly Field[] = [
  ['code', 'Saved status code'],
  ['label', 'Saved status label'],
];
const ruleFields: readonly Field[] = [
  ['id', 'Rule ID'],
  ['eventCode', 'Saved event code'],
  ['statusCode', 'Saved target status code'],
  ['requirementId', 'Requirement ID'],
  ['sourceNodeId', 'Source node ID'],
  ['outcomeCode', 'Outcome code'],
];
const stepEvents = [
  'step_entered',
  'step_returned',
  'step_completed',
  'form_saved',
  'document_uploaded',
  'decision_recorded',
];
function labelsRow(
  raw: unknown,
  node: Saved,
  ownerUnique: boolean,
): WorkflowPublishedProgressRow {
  const value = object(raw),
    fields: readonly Field[] = [
      ['initialStatusCode', 'Saved initial status code'],
      ['allowManualChanges', 'Saved manual changes setting'],
    ];
  const statuses = group(
    value,
    'statuses',
    'Saved step labels',
    'step-labels',
    (item, index, all) =>
      detail(
        item,
        index,
        'step-labels:status',
        text(object(item)['label'])
          ? display(object(item)['label'])
          : `Saved label ${index + 1}`,
        statusFields,
        [
          ...duplicateIssues(item, all, 'code', 'status code'),
          ...(typeof object(item)['label'] !== 'string'
            ? ['The saved status label has an unsupported shape.']
            : []),
          ...(text(object(item)['label']) &&
          all.filter(
            (candidate) => object(candidate)['label'] === object(item)['label'],
          ).length > 1
            ? ['Duplicate saved status label. Every indexed label is retained.']
            : []),
        ],
      ),
  );
  const requirements = object(object(node['declaration'])['behavior'])[
    'completionRequirements'
  ];
  const rules = group(
    value,
    'rules',
    'Saved step label rules',
    'step-labels',
    (item, index, all) => {
      const rule = object(item),
        prefix = `step-labels:rule-${index}`;
      const target = uniqueTarget(
        value['statuses'],
        'code',
        rule['statusCode'],
        ownerUnique,
      );
      const requirement = uniqueTarget(
        requirements,
        'id',
        rule['requirementId'],
        ownerUnique,
      );
      const row = detail(
        item,
        index,
        'step-labels:rule',
        text(rule['id']) ? display(rule['id']) : `Saved rule ${index + 1}`,
        ruleFields,
        [
          ...duplicateIssues(item, all, 'id', 'rule ID'),
          ...(!text(rule['eventCode']) ||
          !stepEvents.includes(rule['eventCode'])
            ? [
                'This saved event is not a known step-owned label event. It is retained without moving it to workflow scope.',
              ]
            : []),
          ...(!target
            ? [
                'The saved target status code does not identify one local status. No caption is guessed.',
              ]
            : []),
          ...(rule['requirementId'] != null && !requirement
            ? [
                'The saved requirement ID does not identify one requirement on this exact step. No requirement is substituted.',
              ]
            : []),
          ...(rule['sourceNodeId'] != null
            ? [
                'A saved source node is retained literally; a step-owned rule already has this exact owner.',
              ]
            : []),
          ...(['requirementId', 'sourceNodeId', 'outcomeCode'].some(
            (key) => rule[key] != null && typeof rule[key] !== 'string',
          )
            ? ['An optional saved rule reference has an unsupported shape.']
            : []),
        ],
      );
      row.fields = [
        ...row.fields,
        ...referenceFacts(
          `${prefix}:status`,
          'status label',
          target,
          target?.raw['label'],
        ),
        ...referenceFacts(
          `${prefix}:requirement`,
          'requirement',
          requirement,
          requirement
            ? `${display(requirement.raw['id'])} (${display(requirement.raw['provider'])}@${display(requirement.raw['version'])})`
            : null,
        ),
      ];
      return row;
    },
  );
  const initial = uniqueTarget(
    value['statuses'],
    'code',
    value['initialStatusCode'],
    ownerUnique,
  );
  const base = detail(
    raw,
    0,
    'step-labels',
    'Saved step labels',
    [...fields, ['statuses', 'Statuses'], ['rules', 'Rules']],
    [
      ...(!text(value['initialStatusCode']) || !initial
        ? [
            'The saved initial status code does not identify one local status. No starting label is generated.',
          ]
        : []),
      ...(typeof value['allowManualChanges'] !== 'boolean'
        ? ['The saved manual changes setting has an unsupported shape.']
        : []),
    ],
  );
  base.fields = [
    ...facts(raw, fields, 'step-labels-0'),
    ...referenceFacts(
      'step-labels:initial',
      'initial status label',
      initial,
      initial?.raw['label'],
    ),
  ];
  return {
    ...base,
    description:
      'Saved business labels and event rules for this step only. These do not describe current engine progress, workflow status or runtime authorization.',
    state:
      base.issues.length ||
      [statuses, rules].some((item) => item.state === 'retained-unknown')
        ? 'retained-unknown'
        : 'available',
    groups: [statuses, rules],
  };
}
function section(
  node: Saved,
  document: Saved,
  ownerUnique: boolean,
  id: WorkflowPublishedProgressSectionId,
): WorkflowPublishedProgressSection {
  const key =
      id === 'progress' ? 'stepProgressConfiguration' : 'statusConfiguration',
    raw = node[key],
    savedPresence = presence(node, key);
  const rows =
    savedPresence === 'value'
      ? [
          id === 'progress'
            ? progressRow(raw, document, ownerUnique)
            : labelsRow(raw, node, ownerUnique),
        ]
      : [];
  return {
    id,
    label: id === 'progress' ? 'Step progress' : 'Step labels',
    presence: savedPresence,
    rows,
    issues: [],
    raw: copy(raw),
    state:
      savedPresence !== 'value'
        ? 'absent'
        : rows.some((row) => row.state === 'retained-unknown')
          ? 'retained-unknown'
          : 'available',
  };
}

/** Immutable saved facts. Reported canonical provenance is not recomputed or treated as runtime authority. */
export function readWorkflowPublishedProgressInspection(
  source: WorkflowPublishedInspectionSource,
  target: { nodeIndex: number; nodeId: string | null },
): WorkflowPublishedProgressInspection {
  const admitted = readWorkflowPublishedInspection(source);
  const unavailable = (
    id: WorkflowPublishedProgressSectionId,
  ): WorkflowPublishedProgressSection => ({
    id,
    label: id === 'progress' ? 'Step progress' : 'Step labels',
    state: 'unavailable',
    presence: 'absent',
    rows: [],
    issues: [],
    raw: null,
  });
  const sections: Record<
    WorkflowPublishedProgressSectionId,
    WorkflowPublishedProgressSection
  > = {
    progress: unavailable('progress'),
    stepLabels: unavailable('stepLabels'),
  };
  const result: WorkflowPublishedProgressInspection = {
    context: JSON.stringify([admitted.context, target]),
    state: 'unavailable',
    provenance: structuredClone(admitted.provenance),
    target: { ...target },
    node: null,
    issues: [...admitted.issues],
    sections,
  };
  if (admitted.state !== 'available') {
    result.issues = [
      ...admitted.issues,
      'Saved progress and labels are withheld because the canonical document was not admitted.',
    ];
    return result;
  }
  const document = object(source.definitionDocument),
    nodes = document['nodes'];
  if (
    !Number.isSafeInteger(target.nodeIndex) ||
    target.nodeIndex < 0 ||
    !Array.isArray(nodes) ||
    target.nodeIndex >= nodes.length
  ) {
    result.issues = [
      'The exact saved node index is unavailable. No other step is substituted.',
    ];
    return result;
  }
  const raw = nodes[target.nodeIndex],
    node = object(raw),
    id = typeof node['id'] === 'string' ? node['id'] : null;
  if (
    !record(raw) ||
    id !== target.nodeId ||
    node['typeCode'] !== 'human_step'
  ) {
    result.issues = [
      'The saved human-step index and identity do not match. Progress and labels are withheld.',
    ];
    return result;
  }
  const unique =
    text(id) && nodes.filter((item) => object(item)['id'] === id).length === 1;
  result.state = 'available';
  result.context = JSON.stringify([admitted.context, target, document]);
  result.issues = unique
    ? []
    : [
        'The saved step ID is missing or duplicated. Indexed facts are retained; cross-reference captions are not guessed.',
      ];
  result.node = {
    index: target.nodeIndex,
    id,
    label:
      typeof node['displayName'] === 'string'
        ? display(node['displayName'])
        : `Saved step ${target.nodeIndex + 1}`,
    typeCode: 'human_step',
    issues: [...result.issues],
    raw: copy(raw),
  };
  sections.progress = section(node, document, unique, 'progress');
  sections.stepLabels = section(node, document, unique, 'stepLabels');
  return result;
}
